import { useMutation } from "@apollo/client";
import type {
  CreateAgentInput as CreateAgentInputType,
  CreateAgentPayload,
  AgentInput,
} from "@casey/db-types";
import { CREATE_CUSTOM_TEMPLATE, GET_ORGANIZATION_AGENTS } from "../gql";

// Convenience type for the hook's API - matches AgentInput but with typed sections
type CreateAgentInput = Pick<
  AgentInput,
  | "name"
  | "description"
  | "goal"
  | "templateSlug"
  | "prompt"
  | "organizationId"
  | "createdByUserId"
  | "greetingTitle"
  | "greetingBody"
> & {
  sections: any; // JSONB array (will be stringified)
  config?: any; // JSONB object (will be stringified)
  flowMeta?: any; // JSONB object for flow editor metadata (will be stringified)
};

interface CreateAgentVariables {
  input: CreateAgentInputType;
}

interface CreateAgentResponse {
  createAgent: CreateAgentPayload;
}

export function useCreateAgent() {
  const [createAgentMutation, { data, loading, error }] = useMutation<
    CreateAgentResponse,
    CreateAgentVariables
  >(CREATE_CUSTOM_TEMPLATE, {
    refetchQueries: (result) => {
      const organizationId = result.data?.createAgent?.agent?.organizationId;
      if (organizationId) {
        return [
          { query: GET_ORGANIZATION_AGENTS, variables: { organizationId } },
        ];
      }
      return [];
    },
  });

  const createAgent = async (agentData: CreateAgentInput) => {
    try {
      const agentInput: AgentInput = {
        name: agentData.name,
        description: agentData.description,
        greetingTitle: agentData.greetingTitle,
        greetingBody: agentData.greetingBody,
        goal: agentData.goal,
        templateSlug: agentData.templateSlug,
        sections: JSON.stringify(agentData.sections),
        config: agentData.config ? JSON.stringify(agentData.config) : undefined,
        flowMeta: agentData.flowMeta
          ? JSON.stringify(agentData.flowMeta)
          : undefined,
        prompt: agentData.prompt,
        organizationId: agentData.organizationId,
        createdByUserId: agentData.createdByUserId,
      };

      const result = await createAgentMutation({
        variables: {
          input: {
            agent: agentInput,
          },
        },
      });

      const createdAgent = result.data?.createAgent?.agent;
      return createdAgent;
    } catch (err) {
      console.error("Error creating agent:", err);
      throw err;
    }
  };

  return {
    createAgent,
    data: data?.createAgent?.agent,
    loading,
    error,
  };
}

export type { CreateAgentInput };

// Backwards compatibility
export { useCreateAgent as useCreateTemplate };
export type { CreateAgentInput as CreateTemplateInput };
